import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import FilterChips, { CONCERNS } from '@/components/Filters';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import HackCard from '@/components/HackCard';
import ProductCard from '@/components/ProductCard';
import ProductDetailDialog from '@/components/ProductDetailDialog';
import { Sparkles, Save, MapPin } from 'lucide-react';

const SKIN_OPTIONS = [
  { value: 'oily', label: 'Oily', desc: 'Shine in T-zone, prone to breakouts' },
  { value: 'dry', label: 'Dry', desc: 'Tight, flaky, sometimes itchy' },
  { value: 'combination', label: 'Combination', desc: 'Oily T-zone, dry cheeks' },
  { value: 'normal', label: 'Normal', desc: 'Balanced, rarely reactive' },
  { value: 'sensitive', label: 'Sensitive', desc: 'Reacts easily, redness-prone' },
];

export default function BeautyProfilePage() {
  const { user, updateProfile } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [skinType, setSkinType] = useState(user?.skin_type || '');
  const [concerns, setConcerns] = useState(user?.concerns || []);
  const [region, setRegion] = useState(user?.region || '');
  const [lifestyle, setLifestyle] = useState(user?.lifestyle || '');
  const [timeBudget, setTimeBudget] = useState(user?.time_budget || '');
  const [budgetPref, setBudgetPref] = useState(user?.budget_pref || '');
  const [glamourLevel, setGlamourLevel] = useState(user?.glamour_level || '');
  const [regions, setRegions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [recHacks, setRecHacks] = useState([]);
  const [recProducts, setRecProducts] = useState([]);
  const [openProduct, setOpenProduct] = useState(null);
  const [conceernPicker, setConcernPicker] = useState(null);

  useEffect(() => {
    setName(user?.name || '');
    setSkinType(user?.skin_type || '');
    setConcerns(user?.concerns || []);
    setRegion(user?.region || '');
    setLifestyle(user?.lifestyle || '');
    setTimeBudget(user?.time_budget || '');
    setBudgetPref(user?.budget_pref || '');
    setGlamourLevel(user?.glamour_level || '');
  }, [user]);

  useEffect(() => {
    api.get('/regions').then(({ data }) => setRegions(data)).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const params = {};
        if (user?.skin_type) params.skin_type = user.skin_type;
        const [hacksRes, productsRes] = await Promise.all([
          api.get('/hacks', { params: { ...params, limit: 6 } }),
          api.get('/products', { params: { ...params, limit: 6 } }),
        ]);
        if (cancelled) return;
        setRecHacks(hacksRes.data.slice(0, 3));
        setRecProducts(productsRes.data.slice(0, 3));
      } catch {}
    };
    if (user) load();
    return () => {
      cancelled = true;
    };
  }, [user?.skin_type, user]);

  const toggleConcern = (val) => {
    if (!val) return;
    setConcerns((prev) =>
      prev.includes(val) ? prev.filter((c) => c !== val) : [...prev, val]
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      await updateProfile({
        name: name || user?.name,
        skin_type: skinType || null,
        concerns,
        region: region || null,
        lifestyle: lifestyle || null,
        time_budget: timeBudget || null,
        budget_pref: budgetPref || null,
        glamour_level: glamourLevel || null,
      });
      toast.success('Profile saved');
    } catch (e) {
      toast.error('Couldn’t save profile');
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <div data-testid="beauty-profile-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
      <div className="max-w-3xl">
        <span className="text-[11px] uppercase tracking-[0.22em] text-rose-gold">Your beauty profile</span>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl font-semibold text-ink leading-[1.05]">
          Hi {user.name || 'love'}, let’s tailor your glow.
        </h1>
        <p className="mt-3 text-muted-foreground leading-relaxed">
          Tell us your skin type and concerns. We’ll quietly filter every hack and product so what you see
          fits you perfectly.
        </p>
      </div>

      <div className="mt-8 grid lg:grid-cols-12 gap-6">
        <Card className="lg:col-span-7 bg-card rounded-2xl border-border/70 p-6 sm:p-8">
          <div className="space-y-6">
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Display name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-2 h-11 rounded-xl bg-card"
                placeholder="What should we call you?"
                data-testid="profile-name-input"
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground flex items-center gap-1">
                <MapPin className="h-3 w-3" /> My region
              </Label>
              <p className="text-xs text-muted-foreground mt-1 mb-2">
                We&rsquo;ll prioritize products that are easy for you to actually buy where you are.
              </p>
              <Select value={region || ''} onValueChange={setRegion}>
                <SelectTrigger className="h-11 rounded-xl bg-card max-w-md" data-testid="profile-region-select">
                  <SelectValue placeholder="Choose your region" />
                </SelectTrigger>
                <SelectContent>
                  {regions.map((r) => (
                    <SelectItem key={r.code} value={r.code} data-testid={`profile-region-option-${r.code}`}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Skin type</Label>
              <RadioGroup
                value={skinType}
                onValueChange={setSkinType}
                className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2"
                data-testid="profile-skin-type-radio-group"
              >
                {SKIN_OPTIONS.map((o) => (
                  <Label
                    key={o.value}
                    htmlFor={`skin-${o.value}`}
                    className={`flex items-start gap-3 rounded-xl border px-4 py-3 cursor-pointer transition-colors ${
                      skinType === o.value
                        ? 'border-rose-soft bg-accent/40'
                        : 'border-border bg-background hover:bg-secondary'
                    }`}
                    data-testid={`profile-skin-type-option-${o.value}`}
                  >
                    <RadioGroupItem value={o.value} id={`skin-${o.value}`} className="mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">{o.label}</p>
                      <p className="text-xs text-muted-foreground">{o.desc}</p>
                    </div>
                  </Label>
                ))}
              </RadioGroup>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Top concerns</Label>
              <div className="mt-3">
                <FilterChips
                  label="Tap to add or remove"
                  value={conceernPicker}
                  onChange={(v) => {
                    toggleConcern(v);
                    setConcernPicker(null);
                  }}
                  options={CONCERNS}
                  testId="profile-concerns-toggle-group"
                />
                {concerns.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {concerns.map((c) => (
                      <Badge
                        key={c}
                        className="bg-accent text-ink border border-rose-tint text-[11px] uppercase tracking-[0.14em] cursor-pointer"
                        onClick={() => toggleConcern(c)}
                      >
                        {c.replace('-', ' ')} ×
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Lifestyle</Label>
              <p className="text-xs text-muted-foreground mt-1 mb-2">Helps us shape a routine that fits your day.</p>
              <Select value={lifestyle || ''} onValueChange={setLifestyle}>
                <SelectTrigger className="h-11 rounded-xl bg-card max-w-md" data-testid="profile-lifestyle-select">
                  <SelectValue placeholder="Choose your lifestyle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="busy">Busy professional</SelectItem>
                  <SelectItem value="wfh">Work from home</SelectItem>
                  <SelectItem value="outdoor">Outdoorsy / commuter</SelectItem>
                  <SelectItem value="parent">Parent / caregiver</SelectItem>
                  <SelectItem value="travel">Frequent traveler</SelectItem>
                  <SelectItem value="student">Student</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Time per routine</Label>
                <Select value={timeBudget || ''} onValueChange={setTimeBudget}>
                  <SelectTrigger className="h-11 rounded-xl bg-card mt-2" data-testid="profile-time-select">
                    <SelectValue placeholder="How long?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5min">5 min · minimal</SelectItem>
                    <SelectItem value="10min">10 min · balanced</SelectItem>
                    <SelectItem value="15min">15 min · indulgent</SelectItem>
                    <SelectItem value="20min+">20 min+ · full ritual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Budget</Label>
                <Select value={budgetPref || ''} onValueChange={setBudgetPref}>
                  <SelectTrigger className="h-11 rounded-xl bg-card mt-2" data-testid="profile-budget-select">
                    <SelectValue placeholder="Spend preference" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="essential">Essential picks</SelectItem>
                    <SelectItem value="mix">Mix of both</SelectItem>
                    <SelectItem value="premium">Premium luxe</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Glamour level</Label>
                <Select value={glamourLevel || ''} onValueChange={setGlamourLevel}>
                  <SelectTrigger className="h-11 rounded-xl bg-card mt-2" data-testid="profile-glamour-select">
                    <SelectValue placeholder="Makeup vibe" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="minimal">Minimal · no-makeup</SelectItem>
                    <SelectItem value="natural">Natural · everyday glow</SelectItem>
                    <SelectItem value="full">Full · expressive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="pt-2">
              <Button
                onClick={save}
                disabled={saving}
                className="rounded-xl bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="profile-save-button"
              >
                <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving...' : 'Save profile'}
              </Button>
            </div>
          </div>
        </Card>

        <Card className="lg:col-span-5 bg-cream-paper border-border/70 rounded-2xl p-6 sm:p-8">
          <span className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-rose-gold">
            <Sparkles className="h-3.5 w-3.5" /> Why this matters
          </span>
          <h3 className="mt-2 font-display text-2xl text-ink">A quietly curated feed</h3>
          <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
            Once your profile is set, your <Link to="/diy" className="underline underline-offset-4">DIY Lab</Link> and{' '}
            <Link to="/products" className="underline underline-offset-4">Curator</Link> automatically prioritize what works for you.
            Save what you love with the heart icon — they’ll appear in <Link to="/favorites" className="underline underline-offset-4">My Favorites</Link>.
          </p>
        </Card>
      </div>

      {/* Recommendations preview */}
      {(recHacks.length > 0 || recProducts.length > 0) && (
        <section className="mt-12">
          <div className="flex items-end justify-between mb-5">
            <div>
              <span className="text-[11px] uppercase tracking-[0.18em] text-rose-gold">Recommended for you</span>
              <h2 className="mt-2 font-display text-3xl font-semibold text-ink">Glow picks based on your profile</h2>
            </div>
          </div>
          {recHacks.length > 0 && (
            <div className="mb-8">
              <h3 className="text-sm font-medium uppercase tracking-[0.14em] text-muted-foreground mb-3">DIY Hacks</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {recHacks.map((h) => (
                  <HackCard key={h.id} hack={h} />
                ))}
              </div>
            </div>
          )}
          {recProducts.length > 0 && (
            <div>
              <h3 className="text-sm font-medium uppercase tracking-[0.14em] text-muted-foreground mb-3">Products</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {recProducts.map((p) => (
                  <ProductCard key={p.id} product={p} onOpen={setOpenProduct} />
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      <ProductDetailDialog
        product={openProduct}
        open={!!openProduct}
        onOpenChange={(o) => !o && setOpenProduct(null)}
      />
    </div>
  );
}
